import React from "react";

import {
    Badge,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Radio,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    CircularProgress
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CasinoIcon from "@mui/icons-material/Casino";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import { useLanguage } from "../../config/language/language";
import { queryMenu } from "../../helper-functions/api/menuQueries";
import { drinkMatchesType } from "../../helper-functions/shared/guestPrefs";
import {
    isFavorite,
    resolveFavorites,
    toggleFavorite as toggleFavoriteEntry
} from "../../helper-functions/shared/favorites";
import CocktailImage from "./components/CocktailImage";
import MenuTour from "./components/MenuTour";
import MenuAccessMessage from "../../helper-components/misc/MenuAccessMessage";
import { hasSeenMenuTour, markMenuTourSeen } from "../../helper-functions/shared/menuTour";

const SELECT_MENU_PROPS = {
    PaperProps: {
        sx: {
            maxHeight: { xs: "45vh", sm: 360 }
        }
    }
};

function cocktailMatchesFilters(drink, { search, baseSpirit, ingredients, readyOnly, drinkType }) {
    if (!drinkMatchesType(drink, drinkType)) return false;

    if (readyOnly && !drink.readyToMake) return false;

    if (search) {
        const hay = `${drink.name} ${drink.ingredients} ${drink.baseIngredient}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
    }

    if (baseSpirit && drink.baseIngredient !== baseSpirit) return false;

    if (ingredients.length) {
        const ingLower = (drink.ingredients || "").toLowerCase();
        const allPresent = ingredients.every((ing) => ingLower.includes(ing.toLowerCase()));
        if (!allPresent) return false;
    }

    return true;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function MenuPager({ id, LANG, page, pageCount, pageSize, total, onPageChange, onPageSizeChange }) {
    const from = total === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(total, (page + 1) * pageSize);
    const labelId = `menu-page-size-${id}`;

    return (
        <Stack spacing={0.5} alignItems="center" sx={{ width: "100%", py: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                <IconButton
                    size="small"
                    color="primary"
                    aria-label={LANG.MENU_PAGE_PREV}
                    disabled={page <= 0}
                    onClick={() => onPageChange(page - 1)}
                    sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" }
                    }}>
                    <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, minWidth: 72, textAlign: "center" }}>
                    {LANG.menuPageOf(page + 1, pageCount)}
                </Typography>
                <IconButton
                    size="small"
                    color="primary"
                    aria-label={LANG.MENU_PAGE_NEXT}
                    disabled={page >= pageCount - 1}
                    onClick={() => onPageChange(page + 1)}
                    sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" }
                    }}>
                    <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                <Typography variant="caption" color="text.secondary">
                    {LANG.MENU_PAGE_SIZE}
                </Typography>
                <Select
                    id={labelId}
                    size="small"
                    variant="standard"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    sx={{ fontSize: "0.8125rem", minWidth: 48 }}>
                    {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                            {n}
                        </MenuItem>
                    ))}
                </Select>
                <Typography variant="caption" color="text.secondary">
                    {LANG.menuPageRange(from, to, total)}
                </Typography>
            </Stack>
        </Stack>
    );
}

function SelectCloseHeader({ onClose, label }) {
    return (
        <ListSubheader
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                lineHeight: "48px",
                bgcolor: "background.paper",
                borderBottom: 1,
                borderColor: "divider"
            }}>
            <Typography variant="subtitle2" color="text.secondary" noWrap sx={{ pr: 1 }}>
                {label}
            </Typography>
            <IconButton
                size="small"
                edge="end"
                aria-label={label}
                onMouseDown={(e) => {
                    // Prevent Select from treating this as a menu item selection
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </ListSubheader>
    );
}

export default function MenuPage(props) {
    const LANG = useLanguage();
    const menuData = props.menu.menuData;

    const [view, setView] = React.useState("curated");
    const [filtersOpen, setFiltersOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [baseSpirit, setBaseSpirit] = React.useState("");
    const [ingredients, setIngredients] = React.useState([]);
    const [basesOpen, setBasesOpen] = React.useState(false);
    const [ingredientsOpen, setIngredientsOpen] = React.useState(false);
    const [readyOnly, setReadyOnly] = React.useState(true);
    const [showRating, setShowRating] = React.useState(true);
    const [selected, setSelected] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [tourOpen, setTourOpen] = React.useState(false);
    const listTopRef = React.useRef(null);

    React.useEffect(() => {
        if (!menuData && props.server.isConnectionToServerEstablished) {
            queryMenu(props);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.server.isConnectionToServerEstablished, menuData, props.prefs?.guestPrefs?.venue]);

    // First-visit spotlight tour once menu chrome is on screen
    React.useEffect(() => {
        if (!menuData || hasSeenMenuTour()) return undefined;
        const t = window.setTimeout(() => setTourOpen(true), 450);
        return () => window.clearTimeout(t);
    }, [menuData]);

    // Migrate legacy favorite ids → cocktail names once menu is available
    React.useEffect(() => {
        if (!menuData || !props.favorites?.setFavorites) return;
        const current = props.favorites?.favorites || [];
        const { favorites: next } = resolveFavorites(current, menuData);
        const same = next.length === current.length && next.every((name, i) => name === current[i]);
        if (!same) props.favorites.setFavorites(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuData]);

    React.useEffect(() => {
        setPage(0);
    }, [view, search, baseSpirit, ingredients, readyOnly, props.prefs?.guestPrefs?.drinkType]);

    const tourSteps = React.useMemo(
        () => [
            {
                selector: "[data-tour='menu-view']",
                title: LANG.MENU_TOUR_VIEW_TITLE,
                body: LANG.MENU_TOUR_VIEW_BODY
            },
            {
                selector: "[data-tour='menu-surprise']",
                title: LANG.MENU_TOUR_SURPRISE_TITLE,
                body: LANG.MENU_TOUR_SURPRISE_BODY
            },
            {
                selector: "[data-tour='menu-filters']",
                title: LANG.MENU_TOUR_FILTERS_TITLE,
                body: LANG.MENU_TOUR_FILTERS_BODY
            },
            {
                selector: "[data-tour='menu-favorite']",
                title: LANG.MENU_TOUR_FAVORITE_TITLE,
                body: LANG.MENU_TOUR_FAVORITE_BODY
            },
            {
                selector: "[data-tour='menu-ask']",
                title: LANG.MENU_TOUR_ASK_TITLE,
                body: LANG.MENU_TOUR_ASK_BODY
            }
        ],
        [LANG]
    );

    const goToPage = (next) => {
        setPage(next);
        const el = listTopRef.current;
        if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    if (!menuData) {
        const menuError = props.menu.menuError;
        const requiresLogin = Boolean(props.auth?.authStatus?.requiresLogin);
        const isLoggedIn = Boolean(props.user?.isLoggedIn);
        const showAccessGate = menuError || (requiresLogin && !isLoggedIn);

        if (showAccessGate) {
            return (
                <MenuAccessMessage
                    error={menuError}
                    requiresLogin={requiresLogin}
                    isLoggedIn={isLoggedIn}
                    onRetry={() => {
                        props.menu.setMenuError?.(null);
                        queryMenu(props);
                    }}
                    onSignIn={() => props.browser.navigate("/login")}
                />
            );
        }

        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 8 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{LANG.MENU_LOADING}</Typography>
            </Box>
        );
    }

    const drinkType = props.prefs?.guestPrefs?.drinkType || null;

    const setDrinkType = (next) => {
        if (!next || !props.prefs?.setGuestPrefs) return;
        props.prefs.setGuestPrefs({
            ...props.prefs.guestPrefs,
            drinkType: next
        });
    };

    const source = view === "curated" ? menuData.curated || [] : menuData.recipes || [];
    const typedRecipes = (menuData.recipes || []).filter((r) => drinkMatchesType(r, drinkType));
    const baseOptions = [...new Set(typedRecipes.map((r) => r.baseIngredient).filter((b) => b && !String(b).startsWith("=")))].sort();
    const ingredientOptions = (menuData.filters || [])
        .map((f) => f.name)
        .filter((name) => name && !/^ingredients?$/i.test(String(name).trim()))
        .sort();

    const filtered = source.filter((drink) => cocktailMatchesFilters(drink, { search, baseSpirit, ingredients, readyOnly, drinkType }));
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
    const safePage = Math.min(page, pageCount - 1);
    const paged = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

    const favorites = props.favorites?.favorites || [];

    const askBartenderAbout = (drink, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        setSelected(null);
        props.browser.navigate("/chat", {
            drinkName: drink.name,
            drinkAskId: Date.now()
        });
    };

    const toggleFavorite = (drink, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        if (!drink?.name || !props.favorites?.setFavorites) return;
        props.favorites.setFavorites(toggleFavoriteEntry(favorites, drink));
    };

    const surpriseMe = () => {
        const pool = (menuData.recipes || []).filter((d) => d.readyToMake && d.onCuratedMenu && drinkMatchesType(d, drinkType));
        if (!pool.length) {
            props.showSnackbar?.({ color: "info", duration: 5000, message: LANG.MENU_SURPRISE_EMPTY });
            return;
        }
        const pick = pool[Math.floor(Math.random() * pool.length)];
        setSelected(pick);
    };

    const activeFilterCount = (search ? 1 : 0) + (baseSpirit ? 1 : 0) + (ingredients.length ? 1 : 0) + (readyOnly ? 1 : 0);

    const completeTour = () => {
        markMenuTourSeen();
        setTourOpen(false);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <Box data-tour="menu-view" sx={{ display: "inline-flex" }}>
                        <ToggleButtonGroup exclusive value={view} onChange={(_, v) => v && setView(v)}>
                            <ToggleButton value="curated">{LANG.MENU_CURATED}</ToggleButton>
                            <ToggleButton value="full">{LANG.MENU_FULL}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
                        <Tooltip title={LANG.MENU_SURPRISE_ME}>
                            <Box data-tour="menu-surprise" sx={{ display: "inline-flex" }}>
                                <IconButton color="primary" aria-label={LANG.MENU_SURPRISE_ME} onClick={surpriseMe}>
                                    <CasinoIcon />
                                </IconButton>
                            </Box>
                        </Tooltip>
                        <Tooltip title={LANG.MENU_FILTERS}>
                            <Box data-tour="menu-filters" sx={{ display: "inline-flex" }}>
                                <IconButton
                                    color={filtersOpen ? "primary" : "default"}
                                    aria-label={LANG.MENU_FILTERS}
                                    aria-expanded={filtersOpen}
                                    onClick={() => setFiltersOpen((open) => !open)}>
                                    <Badge color="secondary" badgeContent={activeFilterCount || undefined} overlap="circular">
                                        <FilterListIcon />
                                    </Badge>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Collapse in={filtersOpen} unmountOnExit>
                    <Stack spacing={2} sx={{ pb: 1 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                                {LANG.MENU_DRINK_TYPE}
                            </Typography>
                            <ToggleButtonGroup exclusive fullWidth size="small" value={drinkType || "alcoholic"} onChange={(_, next) => next && setDrinkType(next)}>
                                <ToggleButton value="alcoholic">{LANG.PREFS_ALCOHOLIC}</ToggleButton>
                                <ToggleButton value="nonAlcoholic">{LANG.PREFS_NON_ALCOHOLIC}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <TextField
                            fullWidth
                            size="small"
                            label={LANG.MENU_SEARCH}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" aria-label={LANG.MENU_CLEAR_SEARCH} edge="end" onClick={() => setSearch("")}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }}
                        />

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="menu-base-spirit-label" shrink>
                                    {LANG.MENU_BASE}
                                </InputLabel>
                                <Select
                                    labelId="menu-base-spirit-label"
                                    open={basesOpen}
                                    onOpen={() => setBasesOpen(true)}
                                    onClose={() => setBasesOpen(false)}
                                    value={baseSpirit || "__any__"}
                                    onChange={(e) => {
                                        const next = e.target.value === "__any__" ? "" : e.target.value;
                                        setBaseSpirit(next);
                                        setBasesOpen(false);
                                    }}
                                    displayEmpty
                                    input={<OutlinedInput notched label={LANG.MENU_BASE} />}
                                    renderValue={(selected) => (!selected || selected === "__any__" ? LANG.MENU_BASE_ANY : selected)}
                                    MenuProps={SELECT_MENU_PROPS}>
                                    <SelectCloseHeader label={LANG.MENU_BASE} onClose={() => setBasesOpen(false)} />
                                    <MenuItem value="__any__">
                                        <Radio checked={!baseSpirit} size="small" tabIndex={-1} sx={{ pointerEvents: "none" }} />
                                        {LANG.MENU_BASE_ANY}
                                    </MenuItem>
                                    {baseOptions.map((b) => (
                                        <MenuItem key={b} value={b}>
                                            <Radio checked={baseSpirit === b} size="small" tabIndex={-1} sx={{ pointerEvents: "none" }} />
                                            {b}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>{LANG.MENU_INGREDIENTS}</InputLabel>
                                <Select
                                    multiple
                                    open={ingredientsOpen}
                                    onOpen={() => setIngredientsOpen(true)}
                                    onClose={() => setIngredientsOpen(false)}
                                    value={ingredients}
                                    onChange={(e) => setIngredients(e.target.value)}
                                    input={<OutlinedInput label={LANG.MENU_INGREDIENTS} />}
                                    renderValue={(selectedVals) => selectedVals.join(", ")}
                                    MenuProps={SELECT_MENU_PROPS}>
                                    <SelectCloseHeader label={LANG.MENU_INGREDIENTS} onClose={() => setIngredientsOpen(false)} />
                                    {ingredientOptions.map((ing) => (
                                        <MenuItem key={ing} value={ing}>
                                            <Checkbox checked={ingredients.includes(ing)} />
                                            {ing}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            <FormControlLabel control={<Checkbox checked={readyOnly} onChange={(e) => setReadyOnly(e.target.checked)} />} label={LANG.MENU_READY} />
                            <FormControlLabel control={<Checkbox checked={showRating} onChange={(e) => setShowRating(e.target.checked)} />} label={LANG.MENU_SHOW_RATING} />
                        </Stack>
                    </Stack>
                </Collapse>

                {filtered.length === 0 ? (
                    <Typography color="text.secondary">{LANG.MENU_EMPTY}</Typography>
                ) : (
                    <>
                        <div ref={listTopRef} />
                        <MenuPager
                            id="top"
                            LANG={LANG}
                            page={safePage}
                            pageCount={pageCount}
                            pageSize={pageSize}
                            total={filtered.length}
                            onPageChange={goToPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                goToPage(0);
                            }}
                        />

                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            {paged.map((drink, drinkIndex) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
                                    <Card sx={{ position: "relative", height: "100%", width: "95%" }}>
                                        <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                                            <Tooltip title={isFavorite(favorites, drink) ? LANG.MENU_UNFAVORITE : LANG.MENU_FAVORITE}>
                                                <Box
                                                    data-tour={drinkIndex === 0 ? "menu-favorite" : undefined}
                                                    sx={{ display: "inline-flex" }}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={isFavorite(favorites, drink) ? LANG.MENU_UNFAVORITE : LANG.MENU_FAVORITE}
                                                        onClick={(e) => toggleFavorite(drink, e)}
                                                        sx={{
                                                            bgcolor: "background.paper",
                                                            boxShadow: 1,
                                                            "&:hover": { bgcolor: "background.paper" }
                                                        }}>
                                                        {isFavorite(favorites, drink) ? (
                                                            <StarIcon fontSize="small" color="warning" />
                                                        ) : (
                                                            <StarBorderIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                            <Tooltip title={LANG.MENU_ASK_BARTENDER}>
                                                <Box
                                                    data-tour={drinkIndex === 0 ? "menu-ask" : undefined}
                                                    sx={{ display: "inline-flex" }}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={LANG.MENU_ASK_BARTENDER}
                                                        onClick={(e) => askBartenderAbout(drink, e)}
                                                        sx={{
                                                            bgcolor: "background.paper",
                                                            boxShadow: 1,
                                                            "&:hover": { bgcolor: "background.paper" }
                                                        }}>
                                                        <AutoAwesomeIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                        <Box
                                            onClick={() => setSelected(drink)}
                                            sx={{ height: "100%", cursor: "pointer" }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setSelected(drink);
                                                }
                                            }}>
                                            <CocktailImage drink={drink} height={120} />
                                            <CardContent>
                                                <Typography variant="h6" noWrap>
                                                    {drink.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {drink.baseIngredient}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                                    {drink.readyToMake && <Chip size="small" color="success" label={LANG.MENU_CHIP_READY} />}
                                                    {drink.onCuratedMenu && <Chip size="small" label={LANG.MENU_CHIP_ON_MENU} />}
                                                    {showRating && drink.rating && <Chip size="small" color="warning" variant="outlined" label={drink.rating} />}
                                                </Stack>
                                            </CardContent>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <MenuPager
                            id="bottom"
                            LANG={LANG}
                            page={safePage}
                            pageCount={pageCount}
                            pageSize={pageSize}
                            total={filtered.length}
                            onPageChange={goToPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                goToPage(0);
                            }}
                        />
                    </>
                )}
            </Stack>

            <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
                {selected && (
                    <>
                        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1 }}>
                            <Typography component="span" variant="h6">
                                {selected.name}
                            </Typography>
                            <Stack direction="row">
                                <Tooltip title={isFavorite(favorites, selected) ? LANG.MENU_UNFAVORITE : LANG.MENU_FAVORITE}>
                                    <IconButton
                                        aria-label={isFavorite(favorites, selected) ? LANG.MENU_UNFAVORITE : LANG.MENU_FAVORITE}
                                        onClick={(e) => toggleFavorite(selected, e)}
                                        color={isFavorite(favorites, selected) ? "warning" : "default"}>
                                        {isFavorite(favorites, selected) ? <StarIcon /> : <StarBorderIcon />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={LANG.MENU_ASK_BARTENDER}>
                                    <IconButton aria-label={LANG.MENU_ASK_BARTENDER} onClick={(e) => askBartenderAbout(selected, e)} color="primary">
                                        <AutoAwesomeIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ mb: 2 }}>
                                <CocktailImage drink={selected} height={230} borderRadius={1} />
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {selected.baseIngredient}
                            </Typography>
                            {showRating && selected.rating && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {LANG.MENU_RATING}: {selected.rating}
                                </Typography>
                            )}
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                                {selected.ingredients}
                            </Typography>
                            {selected.instructions && (
                                <>
                                    <Typography variant="subtitle1">{LANG.MENU_INSTRUCTIONS}</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                                        {selected.instructions}
                                    </Typography>
                                </>
                            )}
                            {selected.notes && (
                                <>
                                    <Typography variant="subtitle1">{LANG.MENU_NOTES}</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {selected.notes}
                                    </Typography>
                                </>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>

            <MenuTour open={tourOpen} steps={tourSteps} LANG={LANG} onComplete={completeTour} />
        </Box>
    );
}
